'use client';

import { useState, useEffect, useRef } from 'react';
import { Type, Check } from 'lucide-react';

interface AccessibilityToggleProps {
  isDark?: boolean;
}

export function AccessibilityToggle({ isDark = false }: AccessibilityToggleProps) {
  const [mounted, setMounted] = useState(false);
  const [fontSize, setFontSize] = useState<'normal' | 'large' | 'xlarge'>('normal');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('trustbox-font-size') as any;
    if (saved === 'large' || saved === 'xlarge') {
      setFontSize(saved);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const changeSize = (size: 'normal' | 'large' | 'xlarge') => {
    // Remove existing classes
    document.documentElement.classList.remove('font-size-large', 'font-size-xlarge');
    
    if (size !== 'normal') {
      document.documentElement.classList.add(`font-size-${size}`);
      localStorage.setItem('trustbox-font-size', size);
    } else {
      localStorage.removeItem('trustbox-font-size');
    }
    
    setFontSize(size);
    setOpen(false);
  };

  if (!mounted) {
    return (
      <div className="h-9 w-9 rounded-full flex items-center justify-center border border-stone-200 opacity-60">
        <Type className="h-4 w-4" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#57534E' }} />
      </div>
    );
  }

  const buttonStyle = {
    color: isDark ? 'rgba(255,255,255,0.7)' : '#57534E',
    backgroundColor: 'transparent',
  };

  const dropdownStyle = {
    background: isDark ? '#102A1A' : '#FFFFFF',
    borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#E7E5E4',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
  };

  const itemHoverClass = isDark ? 'hover:bg-[#0D2617] text-white' : 'hover:bg-[#F5F0EB] text-[#1C1917]';
  const itemActiveColor = isDark ? '#1EB954' : '#004322';

  return (
    <div ref={ref} className="relative inline-block text-left z-50">
      <button
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        className="h-9 w-9 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer"
        style={{
          ...buttonStyle,
          border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #E7E5E4',
        }}
        aria-label="Opções de acessibilidade de texto"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Type className="h-4.5 w-4.5" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Tamanho do texto"
          className="absolute right-0 mt-2 w-48 rounded-xl border p-1 animate-in fade-in slide-in-from-top-2 duration-150"
          style={dropdownStyle}
        >
          <button
            role="option"
            aria-selected={fontSize === 'normal'}
            onClick={() => changeSize('normal')}
            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${itemHoverClass}`}
            style={{ color: fontSize === 'normal' ? itemActiveColor : undefined }}
          >
            <span>Normal (100%)</span>
            {fontSize === 'normal' && <Check className="h-3.5 w-3.5" />}
          </button>
          
          <button
            role="option"
            aria-selected={fontSize === 'large'}
            onClick={() => changeSize('large')}
            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${itemHoverClass}`}
            style={{ color: fontSize === 'large' ? itemActiveColor : undefined }}
          >
            <span>Grande (+15%)</span>
            {fontSize === 'large' && <Check className="h-3.5 w-3.5" />}
          </button>
          
          <button
            role="option"
            aria-selected={fontSize === 'xlarge'}
            onClick={() => changeSize('xlarge')}
            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left ${itemHoverClass}`}
            style={{ color: fontSize === 'xlarge' ? itemActiveColor : undefined }}
          >
            <span>Muito Grande (+25%)</span>
            {fontSize === 'xlarge' && <Check className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}
